import { styled } from 'dripsy';
import React, { Fragment } from 'react';
import { View } from 'react-native';
import { ModalComponentProp } from 'react-native-modalfy';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BaseText } from '../common/BaseText';
import { Icon } from '../common/Icon';
import { IconTouchable } from '../common/IconTouchable';
import { Touchable } from '../common/Touchable';
import { Separator } from '../common/Separator';
import { ModalStackParamsType } from '../../modals/types';
import { BottomModalView } from './components/BottomModalView';
import type { ColorType } from '@types';
import type {
  OptionMenuOptionsType,
  OptionMenuOptionsItemType,
} from '../views/OptionsMenuPopover';

const CrossIconContainer = styled(View)({
  position: 'absolute',
  zIndex: 1,
  elevation: 1,
  top: '$6',
  right: '$9',
});

const Container = styled(View)({
  px: '$9',
  py: '$6',
});

const TouchableContainer = styled(Touchable)({
  flexDirection: 'row',
  alignItems: 'center',
  py: '$6',
});

interface ILabelStyleProps {
  color?: ColorType;
}

const Label = styled(BaseText)((props: ILabelStyleProps) => ({
  color: props.color,
  variant: 'text.medium',
}));

export interface IOptionsMenuModalParams {
  options: OptionMenuOptionsType;
}

type ModalPropsType = ModalComponentProp<
  ModalStackParamsType,
  void,
  'OptionsMenuModal'
>;

export function OptionsMenuModal({ modal }: ModalPropsType) {
  const { bottom } = useSafeAreaInsets();
  const options = modal.getParam('options');

  function closeModal() {
    modal.closeModal();
  }

  function selectOption(item: OptionMenuOptionsItemType) {
    closeModal();
    item.onPress();
  }

  return (
    <BottomModalView bottomInset={bottom}>
      <CrossIconContainer>
        <IconTouchable
          onPress={closeModal}
          name="close-light"
          size={24}
        />
      </CrossIconContainer>

      <Separator size="$4" />
      <Container>
        {options.map((item, index) => (
          <Fragment key={index}>
            {index !== 0 && <Separator size="$6" />}

            <TouchableContainer onPress={() => selectOption(item)}>
              <Icon name={item.icon} size={24} color={item.color} />
              <Separator size="$5" vertical />
              <Label color={item.color}>{item.label}</Label>
            </TouchableContainer>
          </Fragment>
        ))}
      </Container>
    </BottomModalView>
  );
}
